import {
  CloseCircleOutlined,
  DeleteOutlined,
  MessageOutlined,
  RollbackOutlined,
  SlidersOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  Popconfirm,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import type { EventSpeakerWithProfile, SlotRow, SpeakerRole } from '../lib/api';
import type { PresenceInfo } from '../scenes/EventScene';
import { Hand } from './Hand';
import { PresenceDot } from './PresenceDot';
import { RoleSelector } from './RoleSelector';
import {
  SendMessageModal,
  sendMessageToSpeaker,
} from './SendMessageModal';
import { SlideControlPanel } from './SlideControlPanel';

const { Title, Text } = Typography;

export function SlotCard({
  slot,
  speaker,
  presence,
  onVMixChange,
  onRoleChange,
  onTakeOffAir,
  onDelete,
  onRemoveSpeakerFromEvent,
  onSlideControlChange,
}: {
  slot: SlotRow;
  speaker: EventSpeakerWithProfile | undefined;
  presence: PresenceInfo;
  onVMixChange: (vmix: string) => Promise<void>;
  onRoleChange: (role: SpeakerRole) => Promise<void>;
  onTakeOffAir: () => Promise<void>;
  onDelete: () => Promise<void>;
  onRemoveSpeakerFromEvent: () => Promise<void>;
  onSlideControlChange: () => Promise<void> | void;
}) {
  const [slidePanelOpen, setSlidePanelOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [form] = Form.useForm<{ vmix: string }>();

  return (
    <Card
      title={
        <Space>
          <Title level={5} style={{ margin: 0, color: '#0c3643' }}>
            Slot {slot.order_index + 1}
          </Title>
          <Tag color="green">LIVE</Tag>
        </Space>
      }
      extra={
        <Popconfirm title="Remove slot?" okText="Remove" onConfirm={onDelete}>
          <Button size="small" type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      }
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space align="center" size="middle">
          <PresenceDot presence={presence} />
          <div>
            <Space size="small">
              <Text strong style={{ display: 'inline-block' }}>
                {speaker?.name ?? 'Unknown speaker'}
              </Text>
              {speaker?.is_hand_raised === 1 && (
                <Hand size={18} title="Speaker has raised their hand" />
              )}
            </Space>
            {speaker && (
              <RoleSelector value={speaker.role} onChange={onRoleChange} />
            )}
          </div>
        </Space>

        {slot.source_type === 'zoom' ? (
          <div
            style={{
              padding: '8px 12px',
              background: '#f0f5ff',
              border: '1px solid #d6e4ff',
              borderRadius: 6,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              Zoom source
            </Text>
            <div>
              <Text strong>Meeting</Text>{' '}
              <code style={{ fontSize: 12 }}>{slot.zoom_meeting_id ?? '—'}</code>
            </div>
            {slot.zoom_passcode && (
              <div>
                <Text strong>Passcode</Text>{' '}
                <code style={{ fontSize: 12 }}>{slot.zoom_passcode}</code>
              </div>
            )}
          </div>
        ) : (
          <Form
            form={form}
            initialValues={{ vmix: slot.vmix_id ?? '' }}
            onFinish={({ vmix }) => onVMixChange(vmix)}
            layout="inline"
          >
            <Form.Item name="vmix" style={{ flex: 1, marginRight: 8 }}>
              <Input placeholder="vMix ID" />
            </Form.Item>
            <Form.Item>
              <Button htmlType="submit">Update</Button>
            </Form.Item>
          </Form>
        )}

        <Space size="small">
          <Popconfirm
            title="Take this speaker off air?"
            description="They'll go back to the waiting room."
            okText="Take off air"
            okButtonProps={{ danger: true }}
            onConfirm={onTakeOffAir}
          >
            <Tooltip title="Take off air">
              <Button shape="circle" icon={<RollbackOutlined />} />
            </Tooltip>
          </Popconfirm>
          {speaker && (
            <Tooltip title="Send message">
              <Button
                shape="circle"
                icon={<MessageOutlined />}
                onClick={() => setMessageOpen(true)}
              />
            </Tooltip>
          )}
          {speaker && (
            <Tooltip
              title={
                speaker.slide_control_enabled
                  ? 'Slide control (on)'
                  : 'Slide control'
              }
            >
              <Button
                shape="circle"
                icon={
                  <SlidersOutlined
                    style={{
                      color: speaker.slide_control_enabled ? '#428e3e' : undefined,
                    }}
                  />
                }
                onClick={() => setSlidePanelOpen(true)}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Remove this speaker from the event?"
            okText="Remove"
            okButtonProps={{ danger: true }}
            onConfirm={onRemoveSpeakerFromEvent}
          >
            <Tooltip title="Remove from event">
              <Button shape="circle" danger icon={<CloseCircleOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      </Space>
      {speaker && (
        <SlideControlPanel
          open={slidePanelOpen}
          speakerName={speaker.name}
          eventSpeaker={speaker}
          onClose={() => setSlidePanelOpen(false)}
          onChange={onSlideControlChange}
        />
      )}
      {speaker && (
        <SendMessageModal
          open={messageOpen}
          recipient={speaker.name}
          onSend={(payload) =>
            sendMessageToSpeaker(speaker.event_id, speaker.id, payload)
          }
          onClose={() => setMessageOpen(false)}
        />
      )}
    </Card>
  );
}
